import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, TextArea } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface Theme {
  id: string;
  name: string;
  items: string[];
}

export default function AffinityClustersPage() {
  const [themes, setThemes] = useState<Theme[]>([
    { id: '1', name: '', items: [''] },
    { id: '2', name: '', items: [''] },
    { id: '3', name: '', items: [''] },
  ]);
  const [surprises, setSurprises] = useState('');
  const [outliers, setOutliers] = useState('');

  const updateTheme = (id: string, field: 'name', value: string) => {
    setThemes(themes.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const addTheme = () => {
    if (themes.length < 7) {
      setThemes([...themes, { id: Date.now().toString(), name: '', items: [''] }]);
    }
  };

  return (
    <TaskPage
      phase="arrange"
      title="Affinity Clusters"
      pageNumber={1}
      instruction="Sort first, judge later. Move backlog items into 3–7 themes. Rename each theme with verb + outcome (e.g., 'Shorten onboarding time')."
      whyItMatters="First pass silent, then rename & discuss outliers (reduces bias and frees working memory for the task)."
      coachRail={{
        timing: "10 minutes",
        tips: "Create 3-7 themes. Use verb + outcome format for theme names.",
        debrief: "What surprised us? Which items don't fit any theme (park them)?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Theme Cards</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {themes.map((theme, index) => (
              <div key={theme.id} className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-4">
                <TextInput
                  label={`Theme ${index + 1} (verb + outcome)`}
                  value={theme.name}
                  onChange={(e) => updateTheme(theme.id, 'name', e.target.value)}
                  placeholder="e.g., Shorten onboarding time"
                  className="mb-3"
                />
                <TextArea
                  label="Items in this theme"
                  value={theme.items.join('\n')}
                  onChange={(e) => {
                    const updated = [...themes];
                    updated[index].items = e.target.value.split('\n').filter(Boolean);
                    setThemes(updated);
                  }}
                  placeholder="List items that belong here..."
                  rows={6}
                />
              </div>
            ))}
          </div>
          {themes.length < 7 && (
            <button
              onClick={addTheme}
              className="mt-4 w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
            >
              + Add another theme (max 7)
            </button>
          )}
        </div>

        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">Mini-debrief</h3>
          <TextArea
            label="What surprised us?"
            value={surprises}
            onChange={(e) => setSurprises(e.target.value)}
            placeholder="What patterns or groupings were unexpected?"
            rows={3}
          />
          <TextArea
            label="Which items don't fit any theme (park them)?"
            value={outliers}
            onChange={(e) => setOutliers(e.target.value)}
            placeholder="Items that don't fit any theme..."
            rows={3}
            className="mt-4"
          />
        </div>

        <Callout type="tip" phase="arrange">
          <p className="text-sm">Theme names should be verb + outcome (e.g., "Shorten onboarding time", "Reduce customer churn").</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






